import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { BarChart3, Gift, TrendingDown, Trophy } from 'lucide-react';
import { PAYMENT_METHOD_LABELS, type CategoryNode } from '@sebastian/shared';
import { api } from '../lib/api';
import { useCurrency } from '../lib/currency';
import { formatClock, formatDate, pluralise } from '../lib/format';
import { useCategoryTree } from '../hooks/queries';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  SectionHeading,
  Select,
  Spinner,
  Stat,
} from '../components/ui';

/* ------------------------------ date helpers ------------------------------ */

const dayString = (date: Date): string => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.toISOString().slice(0, 10);
};

const daysAgo = (count: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - count);
  return dayString(date);
};

const startOfMonth = (): string => {
  const date = new Date();
  date.setDate(1);
  return dayString(date);
};

type PresetKey = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

const PRESETS: { key: PresetKey; label: string; range: () => { from: string; to: string } }[] = [
  { key: 'today', label: 'Today', range: () => ({ from: daysAgo(0), to: daysAgo(0) }) },
  { key: 'yesterday', label: 'Yesterday', range: () => ({ from: daysAgo(1), to: daysAgo(1) }) },
  { key: 'week', label: 'Last 7 days', range: () => ({ from: daysAgo(6), to: daysAgo(0) }) },
  { key: 'month', label: 'This month', range: () => ({ from: startOfMonth(), to: daysAgo(0) }) },
];

/* ------------------------------ chart pieces ------------------------------ */

/**
 * One ranked row: the bar carries the comparison, the text carries the number.
 *
 * Identity is always in the label, never in the colour, so a single accent is
 * enough for every section and nothing depends on telling two hues apart.
 */
function BarRow({
  label,
  hint,
  value,
  share,
}: {
  label: string;
  hint?: string;
  value: string;
  share: number;
}) {
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-sm text-ink">
          {label}
          {hint ? <span className="ml-2 text-xs text-ink-faint">{hint}</span> : null}
        </span>
        <span className="tnum shrink-0 text-sm font-medium text-ink-soft">{value}</span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-hover">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${Math.max(share * 100, 1.5)}%` }}
        />
      </div>
    </div>
  );
}

/** Change over time: one column per bucket, quiet buckets included. */
function ColumnChart({
  bars,
  labelEvery = 1,
  peakLabel,
}: {
  bars: { key: string; label: string; value: number; title: string }[];
  labelEvery?: number;
  peakLabel: string;
}) {
  const max = Math.max(...bars.map((bar) => bar.value), 1);

  return (
    <div>
      <div className="mb-2 text-right text-[11px] text-ink-faint">Peak {peakLabel}</div>
      <div className="flex h-40 items-stretch gap-[2px]">
        {bars.map((bar) => (
          <div key={bar.key} className="flex min-w-0 flex-1 flex-col justify-end" title={bar.title}>
            <div
              className="w-full rounded-t-[4px] bg-accent/80 transition-colors hover:bg-accent-strong"
              style={{ height: `${(bar.value / max) * 100}%`, minHeight: bar.value > 0 ? 3 : 0 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-[2px]">
        {bars.map((bar, index) => (
          <div
            key={bar.key}
            className="min-w-0 flex-1 truncate text-center text-[10px] text-ink-faint"
          >
            {index % labelEvery === 0 ? bar.label : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------- category filter ----------------------------- */

interface CategoryOption {
  id: string;
  label: string;
  /** This category and every category nested under it. */
  ids: Set<string>;
}

/** The category tree flattened for a select, indented by depth; picking a parent includes its children. */
function flattenCategories(nodes: CategoryNode[], depth = 0): CategoryOption[] {
  return nodes.flatMap((node) => {
    const nested = flattenCategories(node.children, depth + 1);
    const ids = new Set<string>([node.id]);
    for (const child of node.children) ids.add(child.id);
    for (const option of nested) for (const id of option.ids) ids.add(id);
    return [{ id: node.id, label: `${' '.repeat(depth)}${node.name}`, ids }, ...nested];
  });
}

/* --------------------------------- page ----------------------------------- */

/**
 * Management reports.
 *
 * Restricted to owners and managers by `reports:read`. Every money figure counts
 * settled orders only and leaves invited tables out unless "Include invited
 * orders" is ticked — the rules in force are stated on the screen, because a
 * report whose basis is unclear gets argued with.
 */
export function ReportsPage() {
  const { money } = useCurrency();

  const [preset, setPreset] = useState<PresetKey>('today');
  const [from, setFrom] = useState(daysAgo(0));
  const [to, setTo] = useState(daysAgo(0));
  const [includeInvited, setIncludeInvited] = useState(false);
  const [itemCategory, setItemCategory] = useState('');

  // Inactive categories too: an item sold earlier may sit in one that has since been retired.
  const { data: categoryTree } = useCategoryTree(true);
  const categoryOptions = useMemo(() => flattenCategories(categoryTree ?? []), [categoryTree]);

  const applyPreset = (key: PresetKey) => {
    const found = PRESETS.find((candidate) => candidate.key === key);
    if (!found) return;
    const range = found.range();
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
  };

  const params = useMemo(
    () => ({
      from: new Date(`${from}T00:00:00`).toISOString(),
      to: new Date(`${to}T23:59:59.999`).toISOString(),
      // Day and hour buckets are cut in the restaurant's own time, not UTC.
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      includeInvited: includeInvited ? 'true' : undefined,
    }),
    [from, to, includeInvited],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'sales', params],
    queryFn: () => api.reports.sales(params),
  });

  const totals = data?.totals;
  const selectedCategory = categoryOptions.find((option) => option.id === itemCategory);
  const items = useMemo(
    () =>
      (data?.topItems ?? []).filter(
        (row) => !selectedCategory || (row.categoryId !== null && selectedCategory.ids.has(row.categoryId)),
      ),
    [data, selectedCategory],
  );
  const soldItemCount = items.filter((row) => row.quantity > 0).length;
  const maxItemSales = Math.max(...items.map((row) => row.salesCents), 1);
  const maxCategory = Math.max(...(data?.categories ?? []).map((row) => row.salesCents), 1);
  const maxPayment = Math.max(...(data?.paymentMethods ?? []).map((row) => row.amountCents), 1);
  const maxSource = Math.max(...(data?.sources ?? []).map((row) => row.grossCents), 1);
  const maxStaff = Math.max(...(data?.staff ?? []).map((row) => row.grossCents), 1);
  const maxTable = Math.max(...(data?.tables ?? []).map((row) => row.grossCents), 1);

  return (
    <div className="mx-auto max-w-[1500px] px-5 py-6">
      <header className="mb-6">
        <h1 className="font-display text-3xl tracking-wide text-ink">Reports</h1>
        <p className="mt-1 text-sm text-ink-faint">
          Settled orders only — a table still in service is not takings yet.{' '}
          {includeInvited
            ? 'Invited tables are counted in every figure below, like any other order.'
            : 'Invited tables are kept out of every money figure and reported separately.'}
        </p>
      </header>

      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((candidate) => (
              <Button
                key={candidate.key}
                size="sm"
                variant={preset === candidate.key ? 'primary' : 'secondary'}
                onClick={() => applyPreset(candidate.key)}
              >
                {candidate.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Field label="From">
              <Input
                type="date"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPreset('custom');
                }}
              />
            </Field>
            <Field label="To">
              <Input
                type="date"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPreset('custom');
                }}
              />
            </Field>
          </div>
          <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={includeInvited}
              onChange={(event) => setIncludeInvited(event.target.checked)}
              className="h-4 w-4 shrink-0 accent-accent"
            />
            <Gift size={15} className={includeInvited ? 'text-accent-strong' : 'text-ink-faint'} />
            Include invited orders
          </label>
        </div>
      </Card>

      {isLoading ? <Spinner label="Building the report…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {data && totals ? (
        <div className="space-y-5">
          {/* ------------------------------ headline ------------------------------ */}
          <Card>
            <div className="flex flex-wrap gap-x-10 gap-y-5">
              <Stat
                label={includeInvited ? 'Gross, invited included' : 'Gross takings'}
                value={money(totals.grossCents)}
                tone="accent"
              />
              <Stat label="Net sales" value={money(totals.netCents)} />
              <Stat label="Discounts" value={money(totals.discountCents)} />
              <Stat label="Service" value={money(totals.serviceChargeCents)} />
              <Stat label="Tax" value={money(totals.taxCents)} />
              <Stat label="Orders" value={totals.orderCount} />
              <Stat label="Guests" value={totals.guestCount} />
              <Stat label="Average order" value={money(totals.averageOrderCents)} />
              <Stat label="Per guest" value={money(totals.averageGuestCents)} />
              <Stat
                label="Table time"
                value={
                  totals.averageDwellMinutes === null ? '—' : `${totals.averageDwellMinutes} min`
                }
              />
              {/* Tips are the staff's, not takings: shown beside the figures, never inside them. */}
              <Stat label="Tips" value={money(totals.tipCents)} />
            </div>

            {data.inService.orderCount > 0 || totals.invitedCount > 0 ? (
              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-4 text-xs text-ink-faint">
                {data.inService.orderCount > 0 ? (
                  <span className="inline-flex items-center gap-2">
                    <Badge tone="caution">In service</Badge>
                    {pluralise(data.inService.orderCount, 'table')} still open, running at{' '}
                    <span className="tnum text-ink-soft">{money(data.inService.runningCents)}</span>{' '}
                    — not counted above.
                  </span>
                ) : null}
                {totals.invitedCount > 0 ? (
                  <span className="inline-flex items-center gap-2">
                    <Badge tone="accent">
                      <Gift size={11} className="mr-1 inline-block align-[-1px]" />
                      Invited
                    </Badge>
                    {pluralise(totals.invitedCount, 'table')} on the house, worth{' '}
                    <span className="tnum text-ink-soft">{money(totals.invitedCents)}</span>
                    {includeInvited ? ' — included above.' : ' — not counted above.'}
                  </span>
                ) : null}
              </div>
            ) : null}
          </Card>

          {/* ---------------------------- top sellers ----------------------------- */}
          <Card>
            <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 font-display text-2xl leading-tight tracking-wide text-ink">
                  <Trophy size={20} className="text-accent" />
                  Top selling items
                </h2>
                <p className="mt-1 text-sm text-ink-faint">
                  Every item on the menu, by sales value before service charge and tax —{' '}
                  {soldItemCount} of {pluralise(items.length, 'item')} sold in this period.
                </p>
              </div>
              <Field label="Category">
                <Select value={itemCategory} onChange={(event) => setItemCategory(event.target.value)}>
                  <option value="">All categories</option>
                  {categoryOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {items.length === 0 ? (
              <EmptyState title={itemCategory ? 'No items in this category' : 'No items on the menu'} />
            ) : (
              // Capped height: the whole menu is listed, so the table scrolls rather than the page.
              <div className="max-h-112 overflow-auto pr-1">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="sticky top-0 z-10 bg-surface text-left text-[11px] uppercase tracking-wider text-ink-faint">
                    <tr>
                      <th className="w-10 py-2 font-semibold">#</th>
                      <th className="py-2 font-semibold">Item</th>
                      <th className="py-2 font-semibold">Category</th>
                      <th className="py-2 text-right font-semibold">Sold</th>
                      <th className="w-[38%] py-2 font-semibold">Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, index) => (
                      <tr
                        key={`${row.menuItemId ?? row.name}-${index}`}
                        className={clsx('border-t border-line', row.quantity === 0 && 'opacity-55')}
                      >
                        <td className="tnum py-2 text-ink-faint">{row.quantity > 0 ? index + 1 : '—'}</td>
                        <td className="py-2 text-ink">{row.name}</td>
                        <td className="py-2 text-ink-faint">{row.categoryName ?? '—'}</td>
                        <td className="tnum py-2 text-right text-ink-soft">{row.quantity}</td>
                        <td className="py-2 pl-4">
                          <div className="flex items-center gap-3">
                            <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-hover">
                              <div
                                className="h-full rounded-full bg-accent"
                                style={{
                                  width:
                                    row.salesCents > 0
                                      ? `${Math.max((row.salesCents / maxItemSales) * 100, 1.5)}%`
                                      : '0%',
                                }}
                              />
                            </div>
                            <span className="tnum w-24 shrink-0 text-right font-medium text-ink-soft">
                              {money(row.salesCents)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* --------------------- category + payment breakdown -------------------- */}
          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <SectionHeading
                title="Sales by category"
                subtitle="Where the money comes from."
              />
              {data.categories.length === 0 ? (
                <EmptyState title="No sales in this period" />
              ) : (
                <div>
                  {data.categories.map((row) => (
                    <BarRow
                      key={row.categoryName}
                      label={row.categoryName}
                      hint={`${row.quantity} sold`}
                      value={money(row.salesCents)}
                      share={row.salesCents / maxCategory}
                    />
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <SectionHeading
                title="How guests paid"
                subtitle="Settled payments, voids excluded — the till reconciliation."
              />
              {data.paymentMethods.length === 0 ? (
                <EmptyState title="No payments in this period" />
              ) : (
                <div>
                  {data.paymentMethods.map((row) => (
                    <BarRow
                      key={row.method}
                      label={PAYMENT_METHOD_LABELS[row.method]}
                      hint={pluralise(row.count, 'payment')}
                      value={money(row.amountCents)}
                      share={row.amountCents / maxPayment}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* ---------------------------- guest mix ------------------------------ */}
          <Card>
            <SectionHeading
              title="Where guests came from"
              subtitle="Source and new vs returning, as tagged when the table was opened."
            />
            <div className="mb-4 flex flex-wrap gap-8">
              <Stat label="New guests" value={pluralise(totals.newGuestOrders, 'table')} />
              <Stat label="Returning" value={pluralise(totals.returnGuestOrders, 'table')} />
              <Stat
                label="Not tagged"
                value={pluralise(
                  Math.max(0, totals.orderCount - totals.newGuestOrders - totals.returnGuestOrders),
                  'table',
                )}
              />
            </div>
            {data.sources.length === 0 ? (
              <EmptyState title="No orders in this period" />
            ) : (
              <div>
                {data.sources.map((row) => (
                  <BarRow
                    key={row.source ?? 'none'}
                    label={row.source ?? 'Not recorded'}
                    hint={[
                      pluralise(row.orderCount, 'order'),
                      pluralise(row.guestCount, 'guest'),
                      `${row.newCount} new · ${row.returnCount} returning`,
                      row.tipCents > 0 ? `tips ${money(row.tipCents)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    value={money(row.grossCents)}
                    share={row.grossCents / maxSource}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* ------------------------------- trends -------------------------------- */}
          <Card>
            <SectionHeading title="Takings by day" action={<BarChart3 size={20} className="text-accent" />} />
            {data.daily.length === 0 ? (
              <EmptyState title="Nothing closed in this period" />
            ) : (
              <ColumnChart
                peakLabel={money(Math.max(...data.daily.map((row) => row.grossCents), 0))}
                labelEvery={Math.ceil(data.daily.length / 12)}
                bars={data.daily.map((row) => ({
                  key: row.date,
                  label: formatDate(`${row.date}T12:00:00`),
                  value: row.grossCents,
                  title: `${formatDate(`${row.date}T12:00:00`)} · ${money(row.grossCents)} · ${pluralise(
                    row.orderCount,
                    'order',
                  )} · ${pluralise(row.guestCount, 'guest')}`,
                }))}
              />
            )}
          </Card>

          <Card>
            <SectionHeading
              title="Busiest hours"
              subtitle="When tables were opened, across the whole period — the staffing picture."
            />
            <ColumnChart
              peakLabel={money(Math.max(...data.hourly.map((row) => row.grossCents), 0))}
              labelEvery={3}
              bars={data.hourly.map((row) => ({
                key: String(row.hour),
                label: `${String(row.hour).padStart(2, '0')}`,
                value: row.grossCents,
                title: `${String(row.hour).padStart(2, '0')}:00 · ${money(row.grossCents)} · ${pluralise(
                  row.orderCount,
                  'table',
                )}`,
              }))}
            />
          </Card>

          {/* --------------------------- staff and tables -------------------------- */}
          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <SectionHeading
                title="By staff"
                subtitle="Credited to whoever opened the table."
              />
              {data.staff.length === 0 ? (
                <EmptyState title="No orders in this period" />
              ) : (
                <div>
                  {data.staff.map((row) => (
                    <BarRow
                      key={row.staffId ?? row.staffName}
                      label={row.staffName}
                      hint={`${pluralise(row.orderCount, 'order')} · avg ${money(row.averageOrderCents)}`}
                      value={money(row.grossCents)}
                      share={row.grossCents / maxStaff}
                    />
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <SectionHeading title="By table" subtitle="Which covers earn their floor space." />
              {data.tables.length === 0 ? (
                <EmptyState title="No orders in this period" />
              ) : (
                <div className="max-h-[22rem] overflow-y-auto pr-1">
                  {data.tables.map((row) => (
                    <BarRow
                      key={row.tableId}
                      label={`Table ${String(row.tableNumber).padStart(2, '0')}`}
                      hint={`${pluralise(row.orderCount, 'order')} · ${pluralise(row.guestCount, 'guest')}`}
                      value={money(row.grossCents)}
                      share={row.grossCents / maxTable}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* ------------------------ write-offs and invites ----------------------- */}
          <Card>
            <SectionHeading
              title="Write-offs"
              subtitle="Everything that left the kitchen or the till without being paid for."
              action={<TrendingDown size={20} className="text-accent" />}
            />
            <div className="flex flex-wrap gap-x-10 gap-y-5">
              <Stat label="Voided items" value={money(data.losses.cancelledCents)} />
              <Stat
                label="Items voided"
                value={`${data.losses.cancelledQuantity} on ${pluralise(
                  data.losses.cancelledItemLines,
                  'line',
                )}`}
              />
              <Stat label="Payments voided" value={money(data.losses.voidedPaymentCents)} />
              <Stat label="Void count" value={data.losses.voidedPaymentCount} />
              <Stat label="Invited away" value={money(totals.invitedCents)} tone="accent" />
            </div>
            <p className="mt-4 text-xs text-ink-faint">
              Voided item values cover every order opened in this period, settled or not, so an
              abandoned table shows up here rather than disappearing. Removed orders are left
              out of the whole report.
            </p>
          </Card>

          {data.invited.length > 0 ? (
            <Card>
              <SectionHeading
                title="Invited tables"
                subtitle={
                  includeInvited
                    ? 'Closed on the house — kept in full, and counted in the figures above.'
                    : 'Closed on the house — kept in full, counted nowhere above.'
                }
                action={<Gift size={20} className="text-accent" />}
              />
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wider text-ink-faint">
                    <tr>
                      <th className="py-2 font-semibold">Order</th>
                      <th className="py-2 font-semibold">Table</th>
                      <th className="py-2 font-semibold">Guests</th>
                      <th className="py-2 font-semibold">Closed</th>
                      <th className="py-2 font-semibold">Opened by</th>
                      <th className="py-2 text-right font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.invited.map((row) => (
                      <tr key={row.id} className="border-t border-line">
                        <td className="tnum py-2 text-ink">#{row.orderNumber}</td>
                        <td className="py-2 text-ink-soft">
                          Table {String(row.tableNumber).padStart(2, '0')}
                        </td>
                        <td className="tnum py-2 text-ink-soft">{row.guestCount}</td>
                        <td className="py-2 text-ink-faint">
                          {row.closedAt
                            ? `${formatDate(row.closedAt)} · ${formatClock(row.closedAt)}`
                            : '—'}
                        </td>
                        <td className="py-2 text-ink-soft">{row.staffName ?? '—'}</td>
                        <td className="tnum py-2 text-right text-ink-soft">
                          {money(row.grandTotalCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
